// React
import React, { Component, RefObject } from "react";
import { VideoAnnotation, RiskData, TextData } from "./type";
// VideoJS
import videojs, { VideoJsPlayer } from "video.js";
import "video.js/dist/video-js.css";

// Components for video.js
import ProfileBox from "./Comps/ProfileBox";
import CustomizableControlBar from "./Comps/CustomizableControlBar";
import RiskTimeline from "./Comps/RiskTimeline";

type Props = {
  videoSrc: string | null;
  videoType: string | null;
  videoWidth: number;
  videoHeight: number;
  videoAnnotations?: VideoAnnotation[];
  videoRisk?: RiskData;
  videoText?: TextData;
  videoStartTime?: number;
  playerWidth?: number;
  playerHeight?: number;
};

type State = {
  videoRisk?: RiskData;
  videoText?: TextData;
};

class VideoTracker extends Component<Props, State> {
  player: VideoJsPlayer | null = null;
  videoTrackerWrapperRef: RefObject<HTMLDivElement> = React.createRef();
  profileBoxes: ProfileBox[] = [];
  constructor(props: Props) {
    super(props);
    this.state = {
      videoRisk: {},
      videoText: [],
    };
    videojs.registerComponent("ProfileBox", ProfileBox);
    videojs.registerComponent("CustomizableControlBar", CustomizableControlBar);
    videojs.registerComponent("RiskTimeline", RiskTimeline);
  }

  componentDidMount(): void {
    const { playerWidth, playerHeight, videoWidth, videoHeight } = this.props;
    // By default, the palyer will fill up the parent container.
    const defaultWidth = this.videoTrackerWrapperRef.current.clientWidth;
    const defaultHeight = this.videoTrackerWrapperRef.current.clientHeight;
    const width = playerWidth || defaultWidth || videoWidth;
    const height = playerHeight || defaultHeight || videoHeight;
    // Transform coordination
    const scale = Math.max(videoWidth / width, videoHeight / height);
    const x0 = (width - videoWidth / scale) / 2;
    const y0 = (height - videoHeight / scale) / 2;
    // Configure player after dom rendered
    this.player = videojs("video-tracker", {
      autoplay: false,
      controls: true,
      width: width,
      height: height,
      children: [],
    });
    this.player.on("timeupdate", () => {
      const currentMileseconds = (this.player?.currentTime() || 0) * 1000;
      // erase all boxes
      this.profileBoxes.forEach((_profile) => {
        _profile.dispose();
      });
      const withinRangeAnnotations = this.props.videoAnnotations?.filter(
        (_annotation) => {
          return (
            _annotation.start <= currentMileseconds &&
            _annotation.start + _annotation.duration > currentMileseconds
          );
        }
      );
      this.profileBoxes =
        withinRangeAnnotations?.map((_annotation) => {
          const left = _annotation.box[0] / scale + x0;
          const top = _annotation.box[1] / scale + y0;
          const width = _annotation.box[2] / scale + x0 - left;
          const height = _annotation.box[3] / scale + y0 - top;
          const entity = _annotation.label;
          const score = _annotation.confidence;
          return this.player?.addChild("ProfileBox", {
            top: top,
            left: left,
            width: width,
            height: height,
            entity: entity,
            score: score,
          }) as ProfileBox;
        }) || [];
    });
    // set up UI components
    this.player.addChild("CustomizableControlBar", {
      width: width,
      height: 60,
    });
  }

  componentDidUpdate(): void {
    if (!this.player) return;
    // update video source
    if (this.props.videoSrc) {
      this.player.src({
        src: this.props.videoSrc,
        type: this.props.videoType || "video/mp4",
      });
    }
    // update player size
    if (this.props.playerWidth) {
      this.player.width(this.props.playerWidth);
    }
    if (this.props.playerHeight) {
      this.player.height(this.props.playerHeight);
    }
    // update player time
    if (this.props.videoStartTime) {
      this.player.currentTime(this.props.videoStartTime);
      this.player.play();
    }
    const { videoRisk, videoText } = this.props;
    if (videoRisk && videoText) {
      this.player.addChild("RiskTimeline", {
        risk: videoRisk,
        text: videoText,
      });
    }
  }

  componentWillUnmount(): void {
    // destroy player instance
    if (this.player) {
      this.player.dispose();
    }
  }

  render(): JSX.Element {
    const { playerWidth, playerHeight } = this.props;
    const width = playerWidth ? `${playerWidth}px` : "100%";
    const height = playerHeight ? `${playerHeight}px` : "100%";
    return (
      <div
        className="video-tracker-wrapper"
        style={{ width: width, height: height }}
        ref={this.videoTrackerWrapperRef}
        data-vjs-player
      >
        <video id="video-tracker" className="video-js" />
      </div>
    );
  }
}

export default VideoTracker;
